import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Phone, Coffee } from "lucide-react";
import { buildAdminApi } from "../../lib/admin";
import LoyaltyDots from "../../components/LoyaltyDots";

const AdminCustomers = () => {
    const { slug } = useParams();
    const adminApi = useMemo(() => buildAdminApi(slug), [slug]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    useEffect(() => {
        adminApi.get("/admin/customers")
            .then((r) => setCustomers(r.data.customers))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    const filtered = customers.filter((c) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return (
            (c.name || "").toLowerCase().includes(s) ||
            (c.phone_last4 || "").includes(s)
        );
    });

    const totalRevenue = customers.reduce((a, b) => a + (b.total_spent || 0), 0);
    const totalCoffees = customers.reduce((a, b) => a + (b.coffees_ordered || 0), 0);

    return (
        <div>
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-2">
                <h1 className="font-display font-black text-3xl">Customers</h1>
                <input
                    data-testid="customer-search"
                    placeholder="Search name or last 4…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="px-4 py-2 rounded-full bg-white border-2 border-[#E8DCCB] focus:border-[#FF4F18] outline-none text-sm font-display font-bold"
                />
            </div>

            {/* Summary */}
            <div className="grid grid-cols-3 gap-3 mb-6">
                <Stat label="Total customers" value={customers.length} />
                <Stat label="Total revenue" value={`$${totalRevenue.toFixed(0)}`} />
                <Stat label="Coffees poured" value={totalCoffees} />
            </div>

            {loading && <div className="text-[#8C7A72] py-8 text-center">Loading…</div>}

            <div className="flex flex-col gap-2">
                {filtered.map((c) => (
                    <div key={c.phone_last4 + "-" + c.last_order_at}
                        data-testid={`customer-${c.phone_last4}`}
                        className="rounded-2xl bg-white border border-[#E8DCCB] p-4 flex items-center gap-4 flex-wrap sm:flex-nowrap">
                        <div className="h-12 w-12 rounded-full bg-[#FF4F18] text-white flex items-center justify-center font-display font-black text-lg shrink-0">
                            {c.name ? c.name[0]?.toUpperCase() : c.phone_last4[0]}
                        </div>
                        <div className="min-w-[140px]">
                            <div className="font-display font-black">
                                {c.name || "Anonymous"}
                                {c.free_coffee_available && (
                                    <Star className="inline h-4 w-4 fill-[#FF4F18] text-[#FF4F18] ml-2 -mt-1" strokeWidth={2} />
                                )}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8C7A72] font-bold flex items-center gap-1 mt-1">
                                <Phone className="h-3 w-3" strokeWidth={2.5} />
                                ···{c.phone_last4}
                            </div>
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <LoyaltyDots stamps={c.stamps} size="sm" />
                            <div className="text-xs text-[#8C7A72] mt-1">
                                {c.stamps}/10 stamps · {c.total_redeemed} redeemed
                            </div>
                        </div>
                        <div className="text-right ml-auto">
                            <div className="font-display font-black text-lg">${c.total_spent.toFixed(2)}</div>
                            <div className="text-[10px] text-[#8C7A72] uppercase tracking-wider">
                                {c.orders_count} orders · {c.coffees_ordered} <Coffee className="inline h-3 w-3" strokeWidth={2.5} />
                            </div>
                        </div>
                    </div>
                ))}
                {!loading && filtered.length === 0 && (
                    <div className="rounded-3xl bg-white border border-[#E8DCCB] p-10 text-center text-[#8C7A72]">
                        {q ? "No matches." : "No customers yet."}
                    </div>
                )}
            </div>
        </div>
    );
};

const Stat = ({ label, value }) => (
    <div className="rounded-2xl bg-white border border-[#E8DCCB] p-4">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#8C7A72] font-semibold">{label}</div>
        <div className="mt-1 font-display font-black text-2xl">{value}</div>
    </div>
);

export default AdminCustomers;
